import { useState } from 'react';
import { api, User } from '../../api';
import { getErrorMessage } from '../../../../shared/src';

interface InviteUserModalProps {
  orgId: string;
  onClose: () => void;
  onSuccess: (user: User) => void;
}

export function InviteUserModal({ orgId, onClose, onSuccess }: InviteUserModalProps) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'user' | 'org_admin'>('user');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await api.inviteUser(orgId, {
        email,
        firstName,
        lastName,
        role,
      });
      onSuccess(result.user);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Invite User</h2>
        <p className="modal-description">
          Send an invitation email to add a new user to this organization.
          They will set their password when they first log in.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="user-email">Email Address</label>
            <input
              type="email"
              id="user-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="user@example.com"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="user-first-name">First Name</label>
              <input
                type="text"
                id="user-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                placeholder="John"
              />
            </div>
            <div className="form-group">
              <label htmlFor="user-last-name">Last Name</label>
              <input
                type="text"
                id="user-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                placeholder="Doe"
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="user-role">Role</label>
            <select
              id="user-role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'user' | 'org_admin')}
            >
              <option value="user">User</option>
              <option value="org_admin">Organization Admin</option>
            </select>
            <small>
              {role === 'org_admin'
                ? 'Can manage users and organization settings'
                : 'Regular user access'}
            </small>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>

        <style>{`
          .modal-description {
            color: #666;
            margin-bottom: 20px;
            font-size: 14px;
          }

          .form-row {
            display: flex;
            gap: 16px;
          }

          .form-row .form-group {
            flex: 1;
          }
        `}</style>
      </div>
    </div>
  );
}
